import React from 'react';
import { ArrowRight, Plus, Users, Briefcase, Crown, Share2, User } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { $api } from '../../../api';
import { useOrganization } from '../../../contexts/OrganizationContext';
import ErrorMessage from '../../ErrorMessage';
import { Relation } from './RelationCard';
import { RelationGroup } from './RelationGroup';

interface IncomingRelationsSectionProps {
  onAddRelation: () => void;
  onEntityClick: (entityId: string) => void;
  onEditRelation: (relation: Relation) => void;
  onDeleteRelation: (relation: Relation) => void;
  isDeleting?: boolean;
}

const groupRelationsByKind = (relations: Relation[]) => {
  const groups: Record<string, Relation[]> = {
    director: [],
    executive: [],
    shareholder: [],
    ubo: [],
    other: [],
  };

  relations.forEach(rel => {
    if (groups[rel.relation_kind]) {
      groups[rel.relation_kind].push(rel);
    } else {
      groups.other.push(rel);
    }
  });

  return groups;
};

export const IncomingRelationsSection: React.FC<IncomingRelationsSectionProps> = ({
  onAddRelation,
  onEntityClick,
  onEditRelation,
  onDeleteRelation,
  isDeleting
}) => {
  const { clientId, organizationId } = useParams<{ clientId: string; organizationId: string }>();
  const { currentOrganization } = useOrganization();
  const orgId = organizationId || currentOrganization?.id || '';
  const entityId = clientId || '';

  // Fetch relations where current entity is the child (incoming relations)
  const childRelationsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}/relations',
    {
      params: {
        path: {
          organization_id: orgId,
          id: entityId,
        },
        query: {
          role: 'child',
        },
      },
    },
    {
      enabled: !!orgId && !!entityId,
    }
  );

  const relations = childRelationsQuery.data?.relations || [];
  const groups = groupRelationsByKind(relations);

  if (childRelationsQuery.isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des actionnaires et dirigeants...</span>
        </div>
      </div>
    );
  }

  if (childRelationsQuery.error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <ErrorMessage error={childRelationsQuery.error} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <ArrowRight className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Actionnaires et dirigeants</h3>
        </div>
        <button
          onClick={onAddRelation}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Ajouter un actionnaire/dirigeant</span>
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Personnes et entreprises qui détiennent des parts ou dirigent cette entreprise
      </p>

      {relations.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Aucun actionnaire ou dirigeant enregistré</p>
        </div>
      ) : (
        <div className="space-y-6">
          <RelationGroup
            title="Directeurs"
            relations={groups.director}
            direction="incoming"
            icon={Briefcase}
            onEntityClick={onEntityClick}
            onEdit={onEditRelation}
            onDelete={onDeleteRelation}
            isDeleting={isDeleting}
          />
          <RelationGroup
            title="Dirigeants"
            relations={groups.executive}
            direction="incoming"
            icon={Crown}
            onEntityClick={onEntityClick}
            onEdit={onEditRelation}
            onDelete={onDeleteRelation}
            isDeleting={isDeleting}
          />
          <RelationGroup
            title="Actionnaires"
            relations={groups.shareholder}
            direction="incoming"
            icon={Share2}
            onEntityClick={onEntityClick}
            onEdit={onEditRelation}
            onDelete={onDeleteRelation}
            isDeleting={isDeleting}
          />
          <RelationGroup
            title="UBO (Bénéficiaires effectifs)"
            relations={groups.ubo}
            direction="incoming"
            icon={Users}
            onEntityClick={onEntityClick}
            onEdit={onEditRelation}
            onDelete={onDeleteRelation}
            isDeleting={isDeleting}
          />
          {groups.other.length > 0 && (
            <RelationGroup
              title="Autres relations"
              relations={groups.other}
              direction="incoming"
              icon={User}
              onEntityClick={onEntityClick}
              onEdit={onEditRelation}
              onDelete={onDeleteRelation}
              isDeleting={isDeleting}
            />
          )}
        </div>
      )}
    </div>
  );
};

